import type { CreateContainerSourceRequest } from '@xver/apis/v1/kufer/containers/model';
import Editor from '@xver/components/Atoms/Editor/Editor';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

export interface InlineSourceFormValue {
    inline: string;
}

interface InlineSourceFormProps {
    value: InlineSourceFormValue;
    onChange: (value: InlineSourceFormValue) => void;
    error?: string;
}

const DEFAULT_DOCKERFILE = `FROM nginx:alpine
COPY . /usr/share/nginx/html
EXPOSE 80
`;

export const DEFAULT_INLINE_SOURCE_VALUE: InlineSourceFormValue = {
    inline: '',
};

export function toCreateSourceRequest(value: InlineSourceFormValue): CreateContainerSourceRequest {
    return {
        type: 'inline',
        inline: value.inline,
    };
}

export default function InlineSourceForm({ value, onChange, error }: InlineSourceFormProps) {
    const { t } = useTranslation('containerEngine');
    const errorId = useId();
    return (
        <div className="w-full flex flex-col gap-2">
            <Editor
                filename="Dockerfile"
                height={360}
                initialValue={value.inline}
                placeholder={DEFAULT_DOCKERFILE}
                onChange={(next) => onChange({ inline: next })}
                textareaProps={{
                    'aria-label': t('deployment.inlineSource.editorAria', 'Dockerfile contents'),
                    'aria-invalid': error ? true : undefined,
                    'aria-describedby': error ? errorId : undefined,
                }}
            />
            <p className="text-xs text-fg/68">
                {t(
                    'deployment.inlineSource.description',
                    'Paste a complete Dockerfile. The contents are base64 encoded before being sent to the build pipeline.'
                )}
            </p>
            {error && (
                <span id={errorId} className="text-xs text-red-400 light:text-red-700">
                    {error}
                </span>
            )}
        </div>
    );
}
